import React, { useEffect, useState } from "react";
import {
  ArrowLeft,
  FileSpreadsheet,
  Package,
  FileText,
} from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../api/axios";
import { formatCurrencyCOP, formatDate } from "../../utils/format";

const labels = {
  draft: "Borrador",
  approved: "Aprobada",
  not_approved: "No aprobada",
  under_review: "Por revisión",
  sent: "Enviada",
  partially_received: "Recepción parcial",
  received: "Recibida",
  cancelled: "Cancelada",
};

function PurchaseOrderDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [entryId, setEntryId] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showSentModal, setShowSentModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");

  useEffect(() => {
    api
      .get(`/purchase-orders/${id}`)
      .then(async (response) => {
        const loadedOrder = response.data;
        setOrder(loadedOrder);
        const relatedEntryId =
          loadedOrder.entry_id || loadedOrder.entries?.[0]?.id;
        if (relatedEntryId) {
          setEntryId(relatedEntryId);
          return;
        }
        try {
          const entriesResponse = await api.get("/entries");
          const relatedEntry = (entriesResponse.data || []).find(
            (entry) => Number(entry.purchase_order_id) === Number(id),
          );
          setEntryId(relatedEntry?.id || null);
        } catch (error) {
          setEntryId(null);
        }
      })
      .catch(() => toast.error("Error al cargar la orden"));
  }, [id]);

  const changeStatus = async (status, rejection_reason) => {
    try {
      const response = await api.patch(`/purchase-orders/${id}/status`, {
        status,
        rejection_reason,
      });
      setOrder((current) => ({ ...current, status: response.data.status }));
      toast.success("Estado actualizado");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "No se pudo actualizar el estado",
      );
    }
  };

  const exportOrder = async () => {
    try {
      const response = await api.get(`/export/purchase-orders/${id}/excel`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const supplierName = (order.supplier?.name || "proveedor")
        .replace(/[\\/:*?"<>|]/g, "-")
        .trim();
      link.download = `orden_compra_${supplierName}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("No se pudo exportar la orden");
    }
  };

  const exportPdf = async () => {
  try {
    const response = await api.get(`/export/purchase-orders/${id}/pdf`, {
      responseType: "blob",
    });

    const url = window.URL.createObjectURL(
      new Blob([response.data], { type: "application/pdf" }),
    );

    const link = document.createElement("a");
    link.href = url;

    const supplierName = (order.supplier?.name || "proveedor")
      .replace(/[\\/:*?"<>|]/g, "-")
      .trim();

    link.download = `orden_compra_${supplierName}.pdf`;

    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(url);
  } catch (error) {
    toast.error("No se pudo exportar la orden a PDF");
  }
};

  if (!order) return <p className="p-6 text-gray-500">Cargando...</p>;
  const getDetailTotal = (detail) => {
    const quantity = ["received", "partially_received"].includes(order.status)
      ? Number(detail.received_quantity || 0)
      : Number(detail.quantity || 0);
    return quantity * Number(detail.unit_price || 0);
  };
  const estimatedTotal = order.details.reduce(
    (sum, detail) => sum + getDetailTotal(detail),
    0,
  );
  return (
    <div className="bg-white p-6 rounded-xl shadow-md">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <button
          onClick={() => navigate("/purchase-orders")}
          className="flex items-center gap-2 text-gray-600"
        >
          <ArrowLeft size={20} /> Volver
        </button>
        <div className="flex gap-2">

          {["draft", "under_review"].includes(order.status) && (
            <>
              <button
                onClick={() => setShowApprovalModal(true)}
                className="bg-green-600 text-white px-4 py-2 rounded"
              >
                Aprobar Orden
              </button>
              <button
                onClick={() => setShowRejectModal(true)}
                className="bg-red-600 text-white px-4 py-2 rounded"
              >
                No Aprobar
              </button>
            </>
          )}
          {order.status === "not_approved" && (
            <>
              <button
                onClick={() => navigate(`/purchase-orders/${id}/edit`)}
                className="bg-yellow-600 text-white px-4 py-2 rounded"
              >
                Editar Orden
              </button>
              <button
                onClick={() => setShowApprovalModal(true)}
                className="bg-green-600 text-white px-4 py-2 rounded"
              >
                Aprobar Orden
              </button>
            </>
          )}
          {order.status === "approved" && (
            <button
              onClick={() => setShowSentModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded"
            >
              Enviar Orden
            </button>
          )}
          {order.status === "sent" && (
            <button
              onClick={() => navigate(`/purchase-orders/${id}/receive`)}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded flex items-center gap-2 shadow-sm"
            >
              <Package size={18} /> Confirmar Ingreso
            </button>
          )}
          {["partially_received", "received"].includes(order.status) &&
            entryId && (
              <Link
                to={`/entries/${entryId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-green-600 text-white px-4 py-2 rounded"
              >
                Ver detalles de entrada
              </Link>
            )}
        </div>
      </div>
      <h2 className="text-3xl font-semibold text-gray-800">
        {order.order_number}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-5 mb-6">
        <div>
          <span className="text-gray-500">Proveedor</span>
          <p className="font-semibold">{order.supplier?.name}</p>
        </div>
        <div>
          <span className="text-gray-500">Fecha</span>
          <p className="font-semibold">{formatDate(order.order_date)}</p>
        </div>
        <div>
          <span className="text-gray-500">Estado</span>
          <p className="font-semibold">{labels[order.status]}</p>
        </div>
        <div>
          <span className="text-gray-500">Creada por</span>
          <p className="font-semibold">{order.user?.name || "-"}</p>
        </div>
      </div>
      <div className="overflow-x-auto rounded-xl shadow">
        <table className="min-w-full text-center">
          <thead className="text-center">
            <tr className="bg-gray-100">
              <th className="p-3 text-left">Producto</th>
              <th className="p-3">Presentación</th>
              <th className="p-3">Solicitado</th>
              <th className="p-3">Recibido</th>
              <th className="p-3">Pendiente</th>
              <th className="p-3">Precio unitario</th>
              <th className="p-3">Total</th>
            </tr>
          </thead>
          <tbody>
            {order.details.map((detail) => (
              <tr key={detail.id} className="border-t">
                <td className="p-3 text-left">{detail.product?.name}</td>
                <td className="p-3">{detail.presentation?.name}</td>
                <td className="p-3">{detail.quantity}</td>
                <td className="p-3">{detail.received_quantity || 0}</td>
                <td className="p-3 font-semibold text-blue-600">
                  {Math.max(
                    0,
                    Number(detail.quantity) -
                      Number(detail.received_quantity || 0),
                  )}
                </td>
                <td className="p-3">{formatCurrencyCOP(detail.unit_price)}</td>
                <td className="p-3">
                  {formatCurrencyCOP(getDetailTotal(detail))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

     <div className="flex items-center justify-between mt-4">
  <div className="flex justify-start gap-3">
    {["approved", "sent"].includes(order.status) && (
      <>
        <button
          onClick={exportOrder}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded flex items-center gap-2 shadow-sm"
        >
          <FileSpreadsheet size={18} />
          Exportar Excel
        </button>

        <button
          onClick={exportPdf}
          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded flex items-center gap-2 shadow-sm"
        >
          <FileText size={18} />
          Exportar PDF
        </button>
      </>
    )}
  </div>

  <div className="text-right text-lg font-bold">
    Total estimado: {formatCurrencyCOP(estimatedTotal)}
  </div>
</div>
      {order.notes && (
        <div className="mt-5 bg-gray-50 p-4 rounded">
          <strong>Notas:</strong> {order.notes}
        </div>
      )}
      {order.status === "not_approved" && order.rejection_reason && (
        <div className="mt-5 bg-red-50 p-4 rounded text-red-700">
          <strong>Motivo de no aprobación:</strong> {order.rejection_reason}
        </div>
      )}
      {showApprovalModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full">
            <h3 className="text-lg font-bold text-gray-900">
              ¿Está seguro de aprobar la orden?
            </h3>
            <p className="text-sm text-gray-500 mt-2">
              La orden quedará lista para ser enviada al proveedor.
            </p>
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setShowApprovalModal(false)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  setShowApprovalModal(false);
                  changeStatus("approved");
                }}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg"
              >
                Sí, aprobar
              </button>
            </div>
          </div>
        </div>
      )}
      {showSentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full">
            <h3 className="text-lg font-bold text-gray-900">
              ¿Está seguro de marcar la orden como enviada?
            </h3>
            <p className="text-sm text-gray-500 mt-2">
              Después podrá registrar la entrada de los insumos.
            </p>
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setShowSentModal(false)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  setShowSentModal(false);
                  changeStatus("sent");
                }}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg"
              >
                Sí, marcar enviada
              </button>
            </div>
          </div>
        </div>
      )}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full">
            <h3 className="text-lg font-bold text-gray-900">
              ¿Está seguro de rechazar la orden?
            </h3>
            <p className="text-sm text-gray-500 mt-2">
              La orden pasará a estado "No aprobada" y podrá ser editada
              nuevamente.
            </p>
            <textarea
              value={rejectionReason}
              onChange={(event) => setRejectionReason(event.target.value)}
              placeholder="Escriba el motivo de la no aprobación"
              rows="4"
              className="w-full mt-4 border rounded-lg px-3 py-2"
            />
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setShowRejectModal(false)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg"
              >
                Cancelar
              </button>
              <button
                disabled={!rejectionReason.trim()}
                onClick={() => {
                  setShowRejectModal(false);
                  changeStatus("not_approved", rejectionReason);
                }}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg disabled:bg-gray-300"
              >
                Sí, rechazar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PurchaseOrderDetails;
